import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import {
  GridFilterModel,
  GridRenderHeaderFilterProps,
  GridSingleSelectColDef,
  GridValueOptionsParams,
  useGridApiContext,
} from '@mui/x-data-grid-pro'
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'

const EMPTY_FILTER_MODEL: GridFilterModel = { items: [] }

type SelectOption = { value: string; label: string; raw: string | number }

const resolveValueOptions = (colDef: GridSingleSelectColDef): unknown => {
  const { valueOptions, field } = colDef
  if (typeof valueOptions === 'function') {
    // Header filter has no row context — model/size already fall back to full unions.
    return valueOptions({ field } as GridValueOptionsParams)
  }
  return valueOptions
}

const toOptions = (valueOptions: unknown): SelectOption[] => {
  if (!Array.isArray(valueOptions)) return []
  return valueOptions.map(option => {
    if (typeof option === 'object' && option !== null) {
      const raw = (option as { value: unknown }).value
      const label = String((option as { label?: unknown }).label ?? raw)
      return { value: String(raw), label, raw: raw as string | number }
    }
    return { value: String(option), label: String(option), raw: option as string | number }
  })
}

const valuesFromFilterValue = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String)
  if (value === null || value === undefined || value === '') return []
  return [String(value)]
}

/** Drop every filter item for a field (URL-restored items often have no DataGrid id). */
export const removeFieldFromFilterModel = (model: GridFilterModel, field: string): GridFilterModel => ({
  ...model,
  items: (model.items ?? []).filter(item => item.field !== field),
})

/**
 * DataGridPro header filters deliberately ignore `isAnyOf` items
 * (`getFilterItem` skips that operator), so `props.item` never reflects
 * multi-select state. Read/write the real filter-model entry instead.
 *
 * Uses Select (not Autocomplete): Autocomplete's portal + input blur races the
 * DataGrid header-filter focus handlers and eats the first option click.
 */
export const SingleSelectHeaderMultiFilter = (props: GridRenderHeaderFilterProps) => {
  const { colDef, item, inputRef } = props
  const apiRef = useGridApiContext()
  const filterModel = apiRef.current?.state.filter.filterModel ?? EMPTY_FILTER_MODEL
  const singleSelectColDef = colDef as GridSingleSelectColDef
  const options = useMemo(
    () => toOptions(resolveValueOptions(singleSelectColDef)),
    // valueOptions may be an array or a row-aware function; field identifies the column.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- colDef identity changes every render
    [colDef.field, singleSelectColDef.valueOptions],
  )
  const placeholder = 'Filter'

  const modelItem = useMemo(() => {
    const forField = filterModel.items.filter(candidate => candidate.field === colDef.field)
    return (
      forField.find(candidate => candidate.operator === 'isAnyOf') ??
      forField[0] ??
      item
    )
  }, [filterModel.items, colDef.field, item])

  const modelValues = useMemo(
    () => valuesFromFilterValue(modelItem.value),
    [modelItem.value],
  )

  const [selectedValues, setSelectedValues] = useState(modelValues)
  const [open, setOpen] = useState(false)
  // DataGrid header-filter focus handlers blur the Select when the menu opens;
  // that blur can call onClose before the first MenuItem click lands.
  const ignoreBlurCloseRef = useRef(false)
  const modelValuesKey = modelValues.join('\0')
  useEffect(() => {
    setSelectedValues(modelValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync on serialized key
  }, [modelValuesKey])

  const applyValues = (values: string[]) => {
    setSelectedValues(values)
    const api = apiRef.current
    if (!api) return
    if (values.length === 0) {
      // Items restored from the URL have no id — deleteFilterItem is a no-op without one.
      const current = api.state.filter.filterModel ?? EMPTY_FILTER_MODEL
      api.setFilterModel(removeFieldFromFilterModel(current, colDef.field))
      return
    }
    // Restore original option types (e.g. year numbers) for isAnyOf matching.
    const typedValues = values.map(
      value => options.find(option => option.value === value)?.raw ?? value,
    )
    api.upsertFilterItem({
      ...modelItem,
      field: colDef.field,
      operator: 'isAnyOf',
      value: typedValues,
    })
  }

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = event.target.value
    applyValues(typeof next === 'string' ? next.split(',') : next)
  }

  return (
    <FormControl
      fullWidth
      size='small'
      sx={{
        height: '100%',
        minWidth: 0,
        flex: 1,
        justifyContent: 'center',
        px: 0.5,
      }}
    >
      <Select
        multiple
        displayEmpty
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          if (ignoreBlurCloseRef.current) {
            ignoreBlurCloseRef.current = false
            return
          }
          setOpen(false)
        }}
        value={selectedValues}
        onChange={handleChange}
        inputRef={inputRef}
        variant='standard'
        disableUnderline
        renderValue={selected => {
          const values = selected as string[]
          if (values.length === 0) return <em>{placeholder}</em>
          return values
            .map(value => options.find(option => option.value === value)?.label ?? value)
            .join(', ')
        }}
        MenuProps={{
          keepMounted: true,
          disableAutoFocusItem: true,
          PaperProps: {
            sx: {
              maxHeight: 280,
              '& .MuiMenuItem-root': {
                fontSize: '0.9rem',
                minHeight: 28,
                py: 0.5,
              },
              '& .MuiMenuItem-root.Mui-selected': {
                fontWeight: 600,
              },
            },
            onMouseDown: (event: MouseEvent) => {
              event.preventDefault()
              ignoreBlurCloseRef.current = true
              // Only suppress the blur-close from this mousedown; allow later outside clicks.
              requestAnimationFrame(() => {
                ignoreBlurCloseRef.current = false
              })
            },
          },
        }}
        sx={{
          width: '100%',
          height: '100%',
          '& .MuiSelect-select': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            py: 0,
            pr: '24px !important',
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value} dense>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
